import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, updateDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, Divider, useTheme, useMediaQuery } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PreInterviewFormModal from '../components/modals/PreInterviewFormModal';
import PreInterviewStatusColumn from '../components/features/PreInterviewStatusColumn';

import PageHeader from '../components/common/PageHeader';
import { Loading } from '../components/common/Loading';

const statuses = ['Yeni', 'Aranacak', 'Geri Arandı'];

const PreInterviews = () => {
    const [interviews, setInterviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInterview, setEditingInterview] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const navigate = useNavigate();

    // YENİ: Mobil ekran kontrolü
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));



    useEffect(() => {
        const q = query(collection(db, "preInterviews"), orderBy("olusturmaTarihi", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInterviews(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const interviewsByStatus = useMemo(() => {
        const grouped = {};
        statuses.forEach(status => grouped[status] = []);
        interviews.forEach(interview => {
            if (statuses.includes(interview.durum)) {
                grouped[interview.durum].push(interview);
            }
        });
        return grouped;
    }, [interviews]);

    const handleMenuOpen = (e, interview) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setSelectedInterview(interview); };
    const handleMenuClose = () => { setAnchorEl(null); setSelectedInterview(null); };

    const handleStatusUpdate = async (newStatus) => {
        if (!selectedInterview) return;
        if (newStatus === 'Aranacak' && selectedInterview.durum !== 'Aranacak') {
            handleOpenModal(selectedInterview);
        } else {
            await updateDoc(doc(db, "preInterviews", selectedInterview.id), { durum: newStatus });
        }
        handleMenuClose();
    };

    const handleConvertToCustomer = async () => {
        if (!selectedInterview || !window.confirm(`${selectedInterview.adSoyad} adlı kişiyi müşteriye dönüştürmek istediğinizden emin misiniz?`)) { handleMenuClose(); return; }
        try {
            const newCustomerRef = await addDoc(collection(db, "customers"), { adSoyad: selectedInterview.adSoyad, telefon: selectedInterview.telefon, email: '', adres: '', eklenmeTarihi: serverTimestamp() });
            await updateDoc(doc(db, "preInterviews", selectedInterview.id), { durum: 'Müşteriye Dönüştü' });
            alert('Müşteriye dönüştürme başarılı!');
            navigate(`/musteri/${newCustomerRef.id}`);
        } catch (error) { console.error("Müşteriye dönüştürme hatası:", error); alert("Bir hata oluştu."); }
        handleMenuClose();
    };

    const handleOpenModal = (interview = null) => { setEditingInterview(interview); setIsModalOpen(true); handleMenuClose(); };

    if (loading) return <Loading message="Ön görüşmeler yükleniyor..." />;

    return (
        <Box sx={{ p: { xs: 1, md: 1 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <PreInterviewFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} interviewToEdit={editingInterview} />

            <PageHeader
                title="Ön Görüşmeler"
                subtitle="Potansiyel müşteri görüşmeleri ve takibi."
                icon={<SupportAgentIcon />}
            >
                <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => handleOpenModal()}>Yeni Ön Görüşme</Button>
            </PageHeader>

            {/* GÜNCELLENDİ: sx prop'u ile mobil ve masaüstü için farklı layout */}
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2,
                flexGrow: 1,
                overflowX: isMobile ? 'hidden' : 'auto',
                overflowY: isMobile ? 'auto' : 'hidden'
            }}>
                {statuses.map(status => (
                    <Box key={status} sx={{
                        flex: isMobile ? '1 1 auto' : '1 1 0',
                        minWidth: isMobile ? 'unset' : 300,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <PreInterviewStatusColumn
                            title={status}
                            interviews={interviewsByStatus[status] || []}
                            onMenuOpen={handleMenuOpen}
                            onCardClick={handleOpenModal}
                        />
                    </Box>
                ))}
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleConvertToCustomer} disabled={selectedInterview?.durum === 'Müşteriye Dönüştü'}>Müşteriye Dönüştür</MenuItem>
                <MenuItem onClick={() => handleOpenModal(selectedInterview)}>Düzenle / Detay</MenuItem>
                <Divider />
                <MenuItem onClick={() => handleStatusUpdate('Yeni')}>"Yeni" Olarak İşaretle</MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('Aranacak')}>"Aranacak" Olarak İşaretle</MenuItem>
                <MenuItem onClick={() => handleStatusUpdate('Geri Arandı')}>"Geri Arandı" Olarak İşaretle</MenuItem>
                <Divider />
                <MenuItem onClick={() => handleStatusUpdate('İptal Oldu')}>İptal Et (Arşivle)</MenuItem>
            </Menu>
        </Box>
    );
};

export default PreInterviews;